import { useCallback, useState } from 'react';

/**
 * State Machine for Inline Edit using discriminated union
 */
export type EditState = { status: 'viewing'; isHovered: boolean } | { status: 'editing' };

/**
 * State transitions for inline editing
 */
const transitions = {
  startEdit: (): EditState => ({ status: 'editing' }),
  finishEdit: (): EditState => ({ status: 'viewing', isHovered: false }),
  hover: (): EditState => ({ status: 'viewing', isHovered: true }),
  unhover: (): EditState => ({ status: 'viewing', isHovered: false }),
};

/**
 * Return type for useInlineEdit hook
 */
export interface UseInlineEditReturn {
  editState: EditState;
  isEditing: boolean;
  isHovered: boolean;
  showPencil: boolean;
  pencilActivated: boolean;
  handleEditClick: () => void;
  handleBlur: () => void;
  handleFocus: () => void;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  /** Programmatically finish editing (useful for click-outside handlers) */
  finishEdit: () => void;
  /** Programmatically cancel editing (reverts changes) */
  cancelEdit: () => void;
}

/**
 * Custom hook for inline edit state machine
 *
 * Manages the edit state using a discriminated union pattern to make
 * impossible states impossible. Provides all the handlers and derived
 * state needed for inline editing components.
 *
 * @param onEditStart - Optional callback when edit mode starts
 * @param onEditFinish - Optional callback when edit mode finishes
 * @param onEditCancel - Optional callback when edit is cancelled (Escape key)
 * @returns State and handlers for inline editing
 */
export function useInlineEdit(
  onEditStart?: () => void,
  onEditFinish?: () => void,
  onEditCancel?: () => void,
): UseInlineEditReturn {
  const [editState, setEditState] = useState<EditState>({ status: 'viewing', isHovered: false });

  const handleEditClick = useCallback(() => {
    setEditState(transitions.startEdit());
    onEditStart?.();
  }, [onEditStart]);

  const finishEdit = useCallback(() => {
    setEditState(transitions.finishEdit());
    onEditFinish?.();
  }, [onEditFinish]);

  const cancelEdit = useCallback(() => {
    setEditState(transitions.finishEdit());
    onEditCancel?.();
  }, [onEditCancel]);

  const handleBlur = useCallback(() => {
    finishEdit();
  }, [finishEdit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        finishEdit();
      } else if (e.key === 'Escape') {
        cancelEdit();
      }
    },
    [finishEdit, cancelEdit],
  );

  const handleFocus = useCallback(() => {
    setEditState(transitions.startEdit());
    onEditStart?.();
  }, [onEditStart]);

  const handleMouseEnter = useCallback(() => {
    if (editState.status === 'viewing') {
      setEditState(transitions.hover());
    }
  }, [editState.status]);

  const handleMouseLeave = useCallback(() => {
    if (editState.status === 'viewing') {
      setEditState(transitions.unhover());
    }
  }, [editState.status]);

  // Derived state
  const isEditing = editState.status === 'editing';
  const isHovered = editState.status === 'viewing' && editState.isHovered;
  const showPencil = isEditing || isHovered;
  const pencilActivated = isEditing;

  return {
    editState,
    isEditing,
    isHovered,
    showPencil,
    pencilActivated,
    handleEditClick,
    handleBlur,
    handleFocus,
    handleMouseEnter,
    handleMouseLeave,
    handleKeyDown,
    finishEdit,
    cancelEdit,
  };
}
